/**
 * Instanssien hallinta (/admin/instances, server-wide): every Kuutio at a
 * glance, and one way into each. A row shows the basics; "Avaa" opens the
 * instance's Yleiskuva, where its stages and settings live
 * (specs/admin-instance-lifecycle.md). "Luo uusi Kuutio" creates one and
 * lands on its Perustiedot; "Tuo Kuutio" makes one from a clone package
 * (specs/clone-packages.md).
 *
 * Labels, archiving and deleting (#1209): the list is fetched with archived
 * instances included (this page is the only one that sees them), the scope
 * buttons split current from archived, and the label chips narrow further.
 * Archiving takes an instance out of every other list; deleting is offered
 * only to an archived one, through DeleteInstanceDialog.
 */
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Plus, Globe, Search, Upload } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import { INSTANCE_LABELS } from '../../../../packages/shared/instanceLabels.js';
import CreateInstanceModal from './CreateInstanceModal.jsx';
import ImportPackagePanel from './Clone/ImportPackagePanel';
import InstanceManagerRow from './InstanceManagerRow';
import DeleteInstanceDialog from './InstanceLabels/DeleteInstanceDialog';
import { LABEL_COLORS } from './InstanceLabels/LabelChips';
import { useNotice } from './DialogProvider.jsx';
import { useTranslation } from '../hooks/useTranslation.jsx';

const SCOPES = {
  current: (i) => !i.archived_at,
  archived: (i) => !!i.archived_at,
  all: () => true,
};

const UNTAGGED = 'untagged';

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  color: #fff;
`;

const Top = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;

  h1 { margin: 0; font-size: 24px; display: flex; align-items: center; gap: 10px; }
  p { margin: 4px 0 0; color: rgba(255, 255, 255, 0.6); font-size: 14px; }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 9px 16px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$primary ? '#2563eb' : '#333')};
  background: ${p => (p.$primary ? '#2563eb' : 'rgba(255, 255, 255, 0.05)')};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover { filter: brightness(1.15); }
  svg { width: 16px; height: 16px; }
`;

const Tools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
`;

const SearchBox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 200px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #333;
  background: rgba(255, 255, 255, 0.04);

  input { flex: 1; min-height: 38px; background: none; border: none; color: #fff; font-size: 14px; outline: none; }
`;

const FilterButton = styled.button`
  min-height: 38px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid ${p => (p.$active ? '#2563eb' : '#333')};
  background: ${p => (p.$active ? 'rgba(37, 99, 235, 0.2)' : 'transparent')};
  color: ${p => (p.$active ? '#fff' : '#aaa')};
  font-size: 13px;
  cursor: pointer;
`;

const LabelFilter = styled.button`
  min-height: 30px;
  padding: 3px 12px;
  border-radius: 999px;
  border: 1px solid ${p => (p.$active ? p.$color : 'rgba(255, 255, 255, 0.2)')};
  background: ${p => (p.$active ? `${p.$color}26` : 'transparent')};
  color: ${p => (p.$active ? p.$color : 'rgba(255, 255, 255, 0.6)')};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const LabelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;

  span { font-size: 12px; color: rgba(255, 255, 255, 0.5); margin-right: 4px; }
`;

const ImportBox = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);

  h2 { margin: 0 0 8px; font-size: 16px; }
`;

const Notice = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
`;

async function post(url) {
  const res = await fetch(url, { method: 'POST', credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function InstanceManager({ onSelectInstance, onCreateInstance }) {
  const { t } = useTranslation();
  const notice = useNotice();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('current');
  const [labels, setLabels] = useState(new Set());
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.INSTANCES({ archived: true }), { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setInstances(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const replace = (row) => setInstances((list) => list.map((i) => (i.id === row.id ? { ...i, ...row } : i)));

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesLabels = (i) => {
      if (labels.size === 0) return true;
      const tags = i.tags || [];
      if (labels.has(UNTAGGED) && tags.length === 0) return true;
      return tags.some((tag) => labels.has(tag));
    };
    return instances
      .filter(SCOPES[scope])
      .filter(matchesLabels)
      .filter((i) => !q || `${i.title} ${i.description || ''}`.toLowerCase().includes(q));
  }, [instances, scope, labels, query]);

  const toggleLabel = (label) => setLabels((set) => {
    const next = new Set(set);
    if (next.has(label)) next.delete(label); else next.add(label);
    return next;
  });

  const handleCreated = async (instance) => {
    setShowCreate(false);
    await load();
    onCreateInstance?.(instance);
  };

  const saveTags = async (i, tags) => {
    const res = await fetch(API_ENDPOINTS.INSTANCE(i.id), {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tags }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    replace(await res.json());
  };

  const archive = async (i) => {
    replace(await post(API_ENDPOINTS.INSTANCE_ARCHIVE(i.id)));
    notice.success(t('instanceList.archived', { title: i.title }));
  };

  const restore = async (i) => {
    replace(await post(API_ENDPOINTS.INSTANCE_RESTORE(i.id)));
    notice.success(t('instanceList.restored', { title: i.title }));
  };

  const deleted = (result) => {
    setInstances((list) => list.filter((i) => i.id !== result.instanceId));
    notice.success(t('instanceList.deleted', { title: result.title, count: result.totalRows }));
    setDeleting(null);
  };

  const untaggedCount = instances.filter(SCOPES[scope]).filter((i) => !(i.tags || []).length).length;

  return (
    <Container>
      <Top>
        <div>
          <h1><Globe size={22} /> {t('instanceList.title')}</h1>
          <p>{t('instanceList.lead')}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button type="button" onClick={() => setShowImport((v) => !v)}>
            <Upload /> {t('clone.importInstance')}
          </Button>
          <Button type="button" $primary onClick={() => setShowCreate(true)}>
            <Plus /> {t('instanceList.create')}
          </Button>
        </div>
      </Top>

      {showImport && (
        <ImportBox>
          <h2>{t('clone.importInstance')}</h2>
          <ImportPackagePanel kind="instance" onOpenClone={(r) => handleCreated({ id: r.id, title: r.name })} />
        </ImportBox>
      )}

      <Tools>
        <SearchBox>
          <Search size={16} color="#888" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('instanceList.search')} />
        </SearchBox>
        {Object.keys(SCOPES).map((key) => (
          <FilterButton key={key} type="button" $active={scope === key} onClick={() => setScope(key)}>
            {t(`instanceList.filter_${key}`)}
          </FilterButton>
        ))}
      </Tools>

      <LabelRow>
        <span>{t('instanceLabels.filterBy')}</span>
        {INSTANCE_LABELS.map((label) => (
          <LabelFilter key={label} type="button" $active={labels.has(label)} $color={LABEL_COLORS[label]}
            onClick={() => toggleLabel(label)} title={t(`instanceLabels.${label}_hint`)}>
            {t(`instanceLabels.${label}`)}
          </LabelFilter>
        ))}
        {untaggedCount > 0 && (
          <LabelFilter type="button" $active={labels.has(UNTAGGED)} $color="#f87171" onClick={() => toggleLabel(UNTAGGED)}>
            {t('instanceLabels.none')} ({untaggedCount})
          </LabelFilter>
        )}
      </LabelRow>

      {loading && <Notice>{t('settingsPages.loading')}</Notice>}
      {error && (
        <Notice>
          {t('instanceList.loadFailed', { error })}{' '}
          <Button type="button" onClick={load}>{t('instanceList.retry')}</Button>
        </Notice>
      )}
      {!loading && !error && shown.length === 0 && (
        <Notice>{instances.length === 0 ? t('instanceList.empty') : t('instanceList.noMatches')}</Notice>
      )}

      {!loading && !error && shown.map((i) => (
        <InstanceManagerRow key={i.id} instance={i}
          onOpen={(row) => onSelectInstance?.(row)}
          onSaveTags={saveTags}
          onArchive={archive}
          onRestore={restore}
          onDelete={setDeleting}
        />
      ))}

      {showCreate && <CreateInstanceModal onClose={() => setShowCreate(false)} onSuccess={handleCreated} />}
      {deleting && <DeleteInstanceDialog instance={deleting} onClose={() => setDeleting(null)} onDeleted={deleted} />}
    </Container>
  );
}

export default InstanceManager;
